import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import {
  getDefaultView,
  getViewsForType,
  isValidView,
} from '@/utils/viewHelpers';
import { ViewPaths, ViewConfig } from '@/types/views';
import { useGetTenant } from '@/api/trafikportalen';
import { useCallback, useMemo } from 'react';
import { DataNotFound } from '@/components/DataNotFound';
import { useIntl, FormattedMessage } from 'react-intl';
import Header from '@/components/Header/Header';
import { HeaderMenu } from '@/components/Header/HeaderMenu';
import { CenteredColumn } from '@/components/CenteredColumn';
import { TenantSettingsSupportForm } from '@/components/TenantSettings/TenantSettingsSupportForm';
import { TenantSettingsConfigForm } from '@/components/TenantSettings/TenantSettingsConfigForm';
import { DocumentHead } from '@/components/core/DocumentHead';
import { Loading } from '@/components/Loading';
import { FullsizePage } from '@/components/FullsizePage';
import { checkUserPermissions } from '@/components/Auth/checkUserPermissions';

export const Route = createFileRoute(
  '/$tenantId/_layout/tenant-settings/$view',
)({
  component: TenantSettingsView,
  beforeLoad: ({ params: { tenantId, view } }) => {
    const hasPermission = checkUserPermissions({
      permission: 'tenant:viewer',
    });
    if (!hasPermission) {
      throw redirect({ to: '/$tenantId', params: { tenantId } });
    }

    if (!isValidView(view, 'tenant-settings')) {
      throw redirect({
        to: '/$tenantId/tenant-settings/$view',
        params: {
          tenantId,
          view: getDefaultView('tenant-settings'),
        },
      });
    }
  },
});

function TenantSettingsView() {
  const { tenantId, view } = Route.useParams();
  const selected = view as ViewPaths<'tenant-settings'>;
  const navigate = useNavigate();

  const { formatMessage } = useIntl();
  const { data: tenant, isLoading } = useGetTenant();

  const onViewChange = useCallback(
    (view?: string) => {
      if (!view) return;

      navigate({
        to: Route.to,
        params: { tenantId, view },
        replace: false,
      });
    },
    [navigate, tenantId],
  );

  const views = getViewsForType('tenant-settings') as ViewConfig[];

  const metadataKeys = useMemo(() => {
    switch (selected) {
      case 'information':
        return {
          titleKey: 'page.tenantSettings.information.title',
          descriptionKey: 'page.tenantSettings.information.description',
        };
      case 'configuration':
        return {
          titleKey: 'page.tenantSettings.configuration.title',
          descriptionKey: 'page.tenantSettings.configuration.description',
        };
      default:
        return {
          titleKey: 'page.tenantSettings.title',
          descriptionKey: 'page.tenantSettings.description',
        };
    }
  }, [selected]);

  if (isLoading) {
    return <Loading />;
  }

  if (!tenant) {
    return (
      <DataNotFound
        description={
          <FormattedMessage defaultMessage="Inställningen för organisationen hittades inte" />
        }
        buttonTitle={<FormattedMessage defaultMessage="Till inställningar" />}
        onClick={() =>
          navigate({ to: '/$tenantId/tenant-settings', params: { tenantId } })
        }
      />
    );
  }

  return (
    <FullsizePage>
      <DocumentHead
        titleKey={metadataKeys.titleKey}
        descriptionKey={metadataKeys.descriptionKey}
        resetOnUnmount={false}
      />
      <Header
        title={formatMessage({ defaultMessage: 'Inställningar' })}
        menu={
          <HeaderMenu
            options={views}
            selected={selected}
            onViewChange={onViewChange}
          />
        }
      />
      <CenteredColumn>
        {selected === 'information' && (
          <div className="flex flex-col gap-4">
            <TenantSettingsSupportForm
              markdown={tenant.faq_company}
              title={formatMessage({ defaultMessage: 'FAQ för företag' })}
              description={formatMessage({
                defaultMessage:
                  'Visas för den som har tillgång till företagsfunktioner.',
              })}
              markdownKey="faq_company"
              tenant={tenant}
            />
            <TenantSettingsSupportForm
              markdown={tenant.faq_school}
              title={formatMessage({ defaultMessage: 'FAQ för skola' })}
              description={formatMessage({
                defaultMessage:
                  'Visas för den som har tillgång till skolfunktioner.',
              })}
              markdownKey="faq_school"
              tenant={tenant}
            />
            <TenantSettingsSupportForm
              markdown={tenant.terms_and_conditions}
              title={formatMessage({
                defaultMessage: 'Allmänna villkor',
              })}
              markdownKey="terms_and_conditions"
              tenant={tenant}
            />
            <TenantSettingsSupportForm
              markdown={tenant.privacy_policy}
              title={formatMessage({
                defaultMessage: 'Integritetspolicy',
              })}
              markdownKey="privacy_policy"
              tenant={tenant}
            />
            <TenantSettingsSupportForm
              markdown={tenant.support}
              title={formatMessage({ defaultMessage: 'Support' })}
              markdownKey="support"
              tenant={tenant}
            />
          </div>
        )}
        {selected === 'configuration' && (
          <TenantSettingsConfigForm tenant={tenant} />
        )}
      </CenteredColumn>
    </FullsizePage>
  );
}
